<script lang="ts">
  // lib
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { DistributorEntity, SellSheetEntity } from "@shared/services/event_tracking/entities";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Spinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";

  // types
  import type { Distributor } from "@shared/types/Distributor";
  import type { PLDistributor, ProductLine, InventoryPrice } from "@shared/types/ProductLine";
  import type { SellSheet } from "@shared/types/SellSheet";
  import type { SellSheetCartWidgetProduct } from "@shared/types/SellSheetProductLine";

  // components
  import PriceWrapper from "@retailer/svelte/Shared/PriceWrapper.svelte";
  import SellSheetBuyPriceQuotes from "@modern/components/retailer/SellSheet/SellSheetBuyPriceQuotes.svelte";
  import RepRecommendationsCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/RepRecommendationsCartWidgetContainer.svelte";

  // props
  export let distributor: Distributor | PLDistributor;
  export let isSFList: boolean;
  export let mobileMode: boolean;
  export let productLine: ProductLine;
  export let sellSheet: SellSheet = null;
  export let sellSheetId: number;

  let cartWidgetShown = false;
  let loadingCartWidget = true;
  let productLineFull: ProductLine;
  let priceDataByInventoryId: Record<number, InventoryPrice> = {};

  const { trackEvent } = EventTracking;

  const displayPriceQuotes =
    isSFList && productLine.sf_price_quotes && productLine.sf_price_quotes.length > 0;
  const trackingEntities = [
    new DistributorEntity({ id: distributor ? distributor.id : null }),
    new SellSheetEntity({ sheetId: sellSheet.id }),
  ];

  const load = async (): Promise<void> => {
    productLineFull = isSFList
      ? productLine
      : await HttpPromise({
          method: "GET",
          url: "/api/retailer/product_lines/" + productLine.cloned_from_product_line_id,
        });
  };

  const getCartWidgetProduct = (sellSheetProduct: SellSheetCartWidgetProduct) => {
    const fullProduct = isSFList
      ? sellSheetProduct
      : productLineFull?.products.find(
          product => product.inventory_id === sellSheetProduct.cloned_from_inventory_id,
        );

    if (cartUpdates[sellSheetProduct.id]) {
      loadingCartWidget = false;
      return cartUpdates[sellSheetProduct.id];
    }
    if (
      !productLineFull ||
      !sellSheetProduct.cloned_from_inventory_id ||
      !cartDistributor ||
      !fullProduct
    ) {
      loadingCartWidget = false;
      return false;
    }
    if (fullProduct.case_price) {
      sellSheetProduct.case_price = fullProduct.case_price;
    }
    if (fullProduct.unit_price) {
      sellSheetProduct.unit_price = fullProduct.unit_price;
    }
    if (fullProduct.inventory_id) {
      sellSheetProduct.inventory_id = fullProduct.inventory_id;
    }
    if (productLineFull.product_type_id) {
      sellSheetProduct.product_type_id = productLineFull.product_type_id;
    }

    cartUpdates[sellSheetProduct.id] = fullProduct;
    loadingCartWidget = false;

    return fullProduct;
  };

  const onPriceFetchComplete = (price: InventoryPrice): void => {
    priceDataByInventoryId[price.inventory_id] = price;
  };

  const showPricesAndCart = (product: SellSheetCartWidgetProduct) => {
    cartWidgetShown =
      getCartWidgetProduct(product) &&
      cartUpdates[product.id] &&
      (!isSFList || product.has_inventory);

    return cartWidgetShown;
  };

  const hasNoPricesAndNeedsThem = (
    product: SellSheetCartWidgetProduct,
    priceDataLookup: Record<number, InventoryPrice>,
    distributorData: Distributor | PLDistributor,
  ): boolean => {
    const cartProduct = getCartWidgetProduct(product);
    const prices = priceDataLookup[cartProduct?.inventory_id];

    return (
      distributorData.sf_requires_a_price_to_order_a_product &&
      prices?.unit_price === null &&
      prices?.case_price === null
    );
  };

  $: if (isSFList && !loadingCartWidget && !cartWidgetShown) {
    const distributor = new DistributorEntity({ id: productLine.distributor?.id });
    const sellSheet = new SellSheetEntity({
      productId: productLine.id,
      productLineId: productLine.cloned_from_product_line_id,
      sfProductVersionId: productLine.sf_product_version_id,
      sheetId: sellSheetId,
    });

    if (!productLine.id) {
      trackEvent("view_product_not_available", [sellSheet, distributor]);
    } else {
      trackEvent("view_visit_product_page", [sellSheet, distributor]);
    }
  }

  const buildSuggestedQuantities = (product: SellSheetCartWidgetProduct) => {
    if (
      (product.case_quantity || product.unit_quantity) &&
      !hasNoPricesAndNeedsThem(product, priceDataByInventoryId, cartDistributor)
    ) {
      return {
        caseQuantity: product.case_quantity,
        unitQuantity: product.unit_quantity,
        containerType: product.container_type,
      };
    }
  };

  $: cartDistributor =
    isSFList || productLineFull?.dual_product ? distributor : productLineFull?.distributor;
  $: cartUpdates = {};
</script>

{#await load()}
  <div class="margin-top--smallest">
    <Spinner />
  </div>
{:then}
  <div class="media">
    <div
      class:distributors__inventory-image={!mobileMode}
      class:margin-right--small={!mobileMode}
      class:media-fixed={!mobileMode}
      class:product-image-class={!mobileMode}
      class:productImage={!mobileMode}
    />
    <div class="media-fluid align-self-center">
      {#if productLine.sell_sheet_products_attributes.length}
        <div
          class="default-body--bold fizz-stack-16 padding-top--smallest"
          data-testid="sellsheet-buy-cart-sizes-label"
        >
          Recommended Sizes:
        </div>
        {#each productLine.sell_sheet_products_attributes as product, i}
          <div class="fizz-bordered fizz-inset-square-16 fizz-stack-8" data-product-id={product.id}>
            <div
              class="fizz-flex-row-responsive fizz-space-between-responsive fizz-align-content-start"
              class:fizz-stack-12={displayPriceQuotes}
            >
              <div class="fizz-flex-item" class:fizz-full-width={mobileMode}>
                <div class="s5">
                  <p class="fizz-text-large fizz-text-strong">
                    {product.container_size}{product.container_units}
                    {product.container_type}
                  </p>
                  <p class="fizz-text-large">
                    {#if product.container_case_size}
                      {`${product.container_case_size} ${pluralize(
                        product.container_type,
                        true,
                      )}/case`}
                    {/if}
                  </p>
                  {#if product.sku}
                    <p class="fizz-text-small fizz-text-subdued">SKU: {product.sku}</p>
                  {/if}
                </div>
              </div>
              <div class="fizz-flex-item s4 fizz-align-self-top" class:fizz-full-width={mobileMode}>
                {#if showPricesAndCart(product)}
                  <PriceWrapper
                    inventoryId={product.inventory_id}
                    productTypeId={product.product_type_id}
                    onFetchComplete={onPriceFetchComplete}
                    hideCustomPrice
                  />
                {/if}
              </div>
              <div class="fizz-flex-item s5 fizz-align-self-top" class:fizz-full-width={mobileMode}>
                {#if showPricesAndCart(product)}
                  {#key cartUpdates[product.id]}
                    <RepRecommendationsCartWidgetContainer
                      inventoryId={product.inventory_id}
                      {trackingEntities}
                      suggestedQuantities={buildSuggestedQuantities(product)}
                    />
                  {/key}
                {:else}
                  <div
                    class="s5 fizz-flex-row"
                    class:fizz-justify-end={!mobileMode}
                    data-testid="sellsheet-buy-cart-product-not-available"
                  >
                    {#if isSFList && !product.id}
                      Product not available on Provi.
                    {:else}
                      Please visit the product page for ordering details.
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
            {#if displayPriceQuotes}
              <div class="fizz-border-top fizz-stack-12" />
              <SellSheetBuyPriceQuotes priceQuotes={productLine.sf_price_quotes} />
            {/if}
          </div>
        {/each}
      {:else if displayPriceQuotes}
        <div class="fizz-bordered fizz-inset-square-16 fizz-stack-8">
          <div
            class="fizz-flex-row-responsive fizz-space-between-responsive fizz-align-content-start"
          >
            <SellSheetBuyPriceQuotes priceQuotes={productLine.sf_price_quotes} />
          </div>
        </div>
      {/if}
    </div>
  </div>
{/await}
